import * as React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { AnimatePresence, motion } from 'motion/react';
import { WifiOff } from 'lucide-react';
import { useStore } from './store/useStore';
import HomePage from './app/page';

// Lazy load all non-essential page routes for great bundle-splitting & instant FCP
const OnboardingPage = React.lazy(() => import('./app/onboarding/OnboardingPage'));
const ProfilePage = React.lazy(() => import('./app/profile/ProfilePage'));
const FotoBahanPage = React.lazy(() => import('./app/cook/foto/page'));
const TwinCookActivePage = React.lazy(() => import('./app/cook/twin/active/page'));
const MPASIActivePage = React.lazy(() => import('./app/cook/mpasi/active/page'));
const FamilyActivePage = React.lazy(() => import('./app/cook/family/active/page'));
const PantryPage = React.lazy(() => import('./app/pantry/page'));
const ShoppingPage = React.lazy(() => import('./app/shopping/page'));
const BudgetModePage = React.lazy(() => import('./app/cook/budget/page'));
const DaruratModePage = React.lazy(() => import('./app/cook/darurat/page'));
const PlanBelanjaModePage = React.lazy(() => import('./app/cook/plan-belanja/page'));
const DesignSystemPage = React.lazy(() => import('./app/design-system/page'));
const TestSupabasePage = React.lazy(() => import('./app/test-supabase/page'));
const LoginPage = React.lazy(() => import('./app/login/page'));

import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import PWAInstallModal from './components/PWAInstallModal';

function PageTracker() {
  const location = useLocation();

  React.useEffect(() => {
    if (ReactGA.isInitialized) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, [location]);

  return null;
}

function PageFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 text-stone-500 font-sans">
      <div className="flex flex-col items-center space-y-4">
        {/* Modern high-performance keyframe-free spinner */}
        <div className="w-8 h-8 rounded-full border-4 border-amber-500/10 border-t-amber-500 animate-spin" />
        <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest animate-pulse">Memuat halaman...</p>
      </div>
    </div>
  );
}

import { useAuthSync } from './lib/auth-sync';

function AuthSyncWrapper({ children }: { children: React.ReactNode }) {
  useAuthSync();
  return <>{children}</>;
}

export default function App() {
  const { hasOnboarded, babyProfile } = useStore();
  const [isOffline, setIsOffline] = React.useState(false);

  React.useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsOffline(!navigator.onLine);
    }

    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined' && !sessionStorage.getItem('satu-wajan-session-start')) {
        sessionStorage.setItem('satu-wajan-session-start', Date.now().toString());
      }
    } catch (e) {
      console.warn('sessionStorage not available (likely iframe):', e);
    }

    // Defer Google Analytics initialization to improve initial load metrics (LCP/FCP)
    // and reduce unused JavaScript blocking early frame renders.
    const initializeGA = () => {
      const gaId = import.meta.env.NEXT_PUBLIC_GA_ID || import.meta.env.VITE_GA_ID;
      if (gaId) {
        ReactGA.initialize(gaId);
        // Track the current initial pageview immediately on deferred GA load
        ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
      }
    };

    // Delay initialization until the main threat of initial render blocking is cleared (after splash screen)
    const runDeferred = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => initializeGA(), { timeout: 3000 });
      } else {
        setTimeout(initializeGA, 1000);
      }
    };

    const timer = setTimeout(runDeferred, 2500);
    return () => clearTimeout(timer);
  }, []);

  // Remove static splash organically after hydration
  React.useEffect(() => {
    const splash = document.getElementById('static-splash-wrapper');
    const updateColors = () => {
      document.body.style.backgroundColor = '#ffffff'; // Match with white headers and bottom bars seamlessly
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', '#ffffff');
      }
    };

    if (splash) {
      const timer = setTimeout(() => {
        // Mulai transisi fade out splash screen secara mulus
        splash.style.transition = 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
        splash.style.opacity = '0';
        
        // Tunggu transisi fade out selesai barulah hapus splash dan ubah background body ke putih
        const removeTimer = setTimeout(() => {
          updateColors();
          splash.remove();
        }, 500);
        
        return () => clearTimeout(removeTimer);
      }, 300); // Sedikit lebih responsif (durasi wait dikurangi dari 500ms ke 300ms)
      return () => clearTimeout(timer);
    } else {
      updateColors();
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthSyncWrapper>
        <Router>
          <PageTracker />
        <main className="relative min-h-screen bg-background font-sans text-foreground antialiased selection:bg-primary selection:text-primary-foreground">
          <AnimatePresence>
            {isOffline && (
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="fixed top-0 left-0 right-0 z-50 bg-amber-600 text-white px-4 py-2 text-xs font-semibold flex items-center justify-center gap-2 shadow-md md:text-sm text-center"
              >
                <WifiOff className="w-4 h-4 shrink-0 animate-bounce" />
                <span>Koneksi terputus. {babyProfile?.caregiverName || 'Mama'} sedang berada dalam Mode Offline — Kulkas & Stepper memasak tetap bisa diakses!</span>
              </motion.div>
            )}
          </AnimatePresence>
          <React.Suspense fallback={<PageFallback />}>
            <Routes>
              <Route 
                path="/" 
                element={hasOnboarded ? <HomePage /> : <Navigate to="/onboarding" replace />} 
              />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/cook/foto" element={<FotoBahanPage />} />
              <Route path="/cook/budget" element={<BudgetModePage />} />
              <Route path="/cook/darurat" element={<DaruratModePage />} />
              <Route path="/cook/plan-belanja" element={<PlanBelanjaModePage />} />
              <Route path="/design-system" element={<DesignSystemPage />} />
              <Route path="/cook/twin/active" element={<TwinCookActivePage />} />
              <Route path="/cook/mpasi/active" element={<MPASIActivePage />} />
              <Route path="/cook/family/active" element={<FamilyActivePage />} />
              <Route path="/pantry" element={<PantryPage />} />
              <Route path="/shopping" element={<ShoppingPage />} />
              <Route path="/test-supabase" element={<TestSupabasePage />} />
              <Route path="/login" element={<LoginPage />} />
              {/* Fallback to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </React.Suspense>
          <Toaster position="top-center" richColors />
          <PWAInstallModal />
        </main>
      </Router>
      </AuthSyncWrapper>
    </ErrorBoundary>
  );
}
